import { openPaymentPage } from "@/lib/open-checkout";
import {
  resolveBillingProvider,
  isAppleManaged,
  APPLE_SUBSCRIPTIONS_URL,
} from "@/lib/billing-provider";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Crown,
  Loader2,
  ExternalLink,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { trackOperational } from "@/lib/track";
import { supabase } from "@/integrations/supabase/client";
import { AdminMembershipPanel } from "@/components/AdminMembershipPanel";
import { UpgradeMembershipCard } from "@/components/membership/UpgradeMembershipCard";
import { ReactivatePaypalMembership } from "@/components/membership/ReactivatePaypalMembership";
import { PaypalCheckoutButton } from "./membership";
import {
  getMyMembership,
  createCustomerPortalSession,
  cancelMyMembership,
} from "@/lib/membership.functions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/billing")({
  validateSearch: (search: Record<string, unknown>): { cancel?: "1" } => ({
    cancel: search.cancel === "1" || search.cancel === 1 ? "1" : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Membership & Billing | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Manage your Recovery Trauma™ Membership, view billing details, and cancel any time.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BillingPage,
});

type Mine = Awaited<ReturnType<typeof getMyMembership>>;

function BillingPage() {
  const { loading, isAuthed, isAdmin } = useMembership();
  const { cancel: cancelParam } = Route.useSearch();
  // Auth state is only known in the browser. Render the same quiet loading
  // state on the server and on the first client render so the page hydrates
  // cleanly (a hydration mismatch here previously stopped the native app's
  // commerce guard from running on this route).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  if (!hydrated || loading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isAuthed) return <PublicBilling />;
  // Admins are never treated as unpaid/pending members — they get the
  // administrator view with the system-testing panel instead.
  if (isAdmin) return <AdminBilling />;
  return <MemberBilling isAdmin={false} autoOpenCancel={cancelParam === "1"} />;
}

/** Administrator view — no activation prompts, no member payment status. */
function AdminBilling() {
  const fetchMine = useServerFn(getMyMembership);
  const [mine, setMine] = useState<Mine | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMine({ data: undefined as never })
      .then((m) => {
        if (!cancelled) setMine(m);
      })
      .catch(() => {
        /* an admin without a membership row is expected */
      });
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setEmail(data.session?.user?.email ?? null);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasStripeSub = !!mine?.stripe_subscription_id;
  const paypal = mine?.paypal ?? null;
  const paypalLive = ["ACTIVE", "APPROVED", "SUSPENDED"].includes(
    (paypal?.status ?? "").toUpperCase(),
  );
  const hasRealSubscription = hasStripeSub || paypalLive;

  return (
    <AdminMembershipPanel
      email={email ?? mine?.email ?? null}
      testSubscription={
        hasRealSubscription ? (
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div className="min-w-0 rounded-xl border border-border bg-muted/20 p-4">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Provider
              </dt>
              <dd className="mt-1 font-medium text-foreground">
                {hasStripeSub ? "Stripe" : "PayPal"}
              </dd>
            </div>
            <div className="min-w-0 rounded-xl border border-border bg-muted/20 p-4">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Subscription status
              </dt>
              <dd className="mt-1 font-medium capitalize text-foreground">
                {hasStripeSub
                  ? (mine?.status ?? "unknown").replace(/_/g, " ")
                  : (paypal?.status ?? "unknown").toLowerCase()}
              </dd>
            </div>
            <div className="min-w-0 rounded-xl border border-border bg-muted/20 p-4">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {mine?.cancel_at_period_end ? "Access ends" : "Next billing date"}
              </dt>
              <dd className="mt-1 font-medium text-foreground">
                {formatDate(mine?.current_period_end ?? null) ?? "—"}
              </dd>
            </div>
            <div className="min-w-0 rounded-xl border border-border bg-muted/20 p-4">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Reference
              </dt>
              <dd className="mt-1 break-all font-medium text-foreground">
                {mine?.stripe_subscription_id ??
                  paypal?.paypal_subscription_id ??
                  "—"}
              </dd>
            </div>
          </dl>
        ) : null
      }
    />
  );
}


function PublicBilling() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-16 md:py-24">
      <div className="rounded-3xl border border-gold/40 bg-cream/40 p-8 text-center shadow-sm md:p-12">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--gradient-gold)]/20 text-[var(--gold-deep)]">
          <Crown className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <h1 className="mt-5 font-serif text-3xl text-foreground md:text-4xl">
          Membership &amp; Billing
        </h1>
        <p className="mt-4 text-sm text-muted-foreground md:text-base">
          Sign in to view your plan, upgrade to yearly, or manage your billing.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3">
          <Link
            to="/login"
            search={{ redirect: "/billing" }}
            className="btn-gold inline-flex items-center gap-2 text-sm"
          >
            Sign in to manage membership
          </Link>
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 text-sm font-medium text-[var(--gold-deep)] underline"
          >
            Join Recovery Trauma™
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function MemberBilling({
  isAdmin,
  autoOpenCancel = false,
}: {
  isAdmin: boolean;
  autoOpenCancel?: boolean;
}) {
  const fetchMine = useServerFn(getMyMembership);
  const portal = useServerFn(createCustomerPortalSession);
  const cancel = useServerFn(cancelMyMembership);

  const [mine, setMine] = useState<Mine | null>(null);
  const [loadingMine, setLoadingMine] = useState(true);
  const [portalPending, setPortalPending] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPending, setCancelPending] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [cancelDetail, setCancelDetail] = useState("");

  const reload = () => {
    setLoadingMine(true);
    fetchMine({ data: undefined as never })
      .then(setMine)
      .catch((err) =>
        toast.error("Couldn't load membership", { description: err instanceof Error ? err.message : String(err) }),
      )
      .finally(() => setLoadingMine(false));
  };

  useEffect(() => {
    reload();
    trackOperational("billing_page_opened", {
      arrived_via: autoOpenCancel ? "cancel_link" : "direct",
      viewport_width: typeof window !== "undefined" ? window.innerWidth : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the cancel modal when arriving via /billing?cancel=1 from the
  // "Cancel membership" link on /my-membership. Only opens once the row loads
  // and the account is genuinely cancellable — otherwise the modal would be
  // meaningless.
  useEffect(() => {
    if (!autoOpenCancel) return;
    if (loadingMine) return;
    if (!mine) return;
    if (mine.cancel_at_period_end) return;
    const paypalIsLive = ["ACTIVE", "APPROVED", "APPROVAL_PENDING", "SUSPENDED"].includes(
      (mine.paypal?.status ?? "").toUpperCase(),
    );
    const stripeLive = !!mine.stripe_subscription_id && mine.status === "active";
    if (!stripeLive && !paypalIsLive) return;
    setCancelOpen(true);
    trackOperational("cancellation_modal_opened", {
      trigger: "cancel_link",
      at: new Date().toISOString(),
    });
  }, [autoOpenCancel, loadingMine, mine]);


  const status = mine?.status ?? null;
  const isActive = status === "active";
  const hasStripe = !!mine?.stripe_subscription_id;
  const hasPaypal = !!mine?.paypal?.paypal_subscription_id;
  const paypalStatus = mine?.paypal?.status ?? null;
  // A PayPal subscription that PayPal itself still considers live — the
  // member must always be able to cancel this, even if our membership row
  // is lagging behind (that lag was the old "PayPal cancellation dead-end").
  const paypalLive =
    hasPaypal &&
    ["ACTIVE", "APPROVED", "APPROVAL_PENDING", "SUSPENDED"].includes(
      (paypalStatus ?? "").toUpperCase(),
    );
  const paypalCancelled = hasPaypal && (paypalStatus ?? "").toUpperCase() === "CANCELLED";
  // Complimentary = active with NO paid subscription on any provider.
  // A PayPal-only member is a paying member, not complimentary.
  const isComp = isActive && !hasStripe && !hasPaypal;
  // Provider of record. Apple-managed subscriptions must never be offered
  // website cancellation or a Stripe/PayPal portal.
  const provider = resolveBillingProvider(mine as never);
  const appleManaged = isAppleManaged(provider);
  const cancelAtPeriodEnd = mine?.cancel_at_period_end === true;
  const nextBilling = formatDate(mine?.current_period_end ?? null);
  const cancelledAt = formatDate(mine?.cancelled_at ?? null);
  // PayPal cannot resume a cancelled subscription, so a member in this state
  // is offered a fresh PayPal checkout instead of being told to email us.
  // Never shown while a PayPal subscription is still live (no duplicates),
  // and never for Apple-managed subscriptions.
  const canReactivatePaypal = !appleManaged && paypalCancelled && !paypalLive && !hasStripe;

  async function openPortal() {
    setPortalPending(true);
    try {
      const r = await portal({ data: {} as never });
      if (r.url) openPaymentPage(r.url);
    } catch (err) {
      toast.error("Couldn't open Stripe portal", { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setPortalPending(false);
    }
  }

  async function confirmCancel() {
    // The reason step is optional — a member must never be blocked from
    // cancelling because they skipped it.
    setCancelPending(true);
    trackOperational("cancellation_confirmed", { reason: cancelReason || "not_shared", at: new Date().toISOString() });
    try {
      const r = await cancel({
        data: {
          reason: cancelReason || undefined,
          reason_detail: cancelDetail.trim() || undefined,
        },
      });
      const when = r.current_period_end
        ? new Date(
            typeof r.current_period_end === "number"
              ? r.current_period_end * 1000
              : r.current_period_end,
          ).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "the end of your current billing period";
      const providerLabel = hasStripe
        ? "the Stripe portal"
        : hasPaypal
        ? "your PayPal account (My Payments → Automatic Payments)"
        : "your account";
      toast.success("Your membership has been cancelled successfully", {
        description: `You can continue using your membership until the end of your current billing period (${when}). After that, you may delete your account. You can resume any time before then from ${providerLabel}.`,
        duration: 10000,
      });

      trackOperational("membership_cancelled", { reason: cancelReason, at: new Date().toISOString() });
      setCancelOpen(false);
      reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      trackOperational("cancellation_failed", { message: msg.slice(0, 200) });
      const isMissing = /no active subscription/i.test(msg);
      toast.error(
        isMissing ? "We couldn't find an active subscription" : "Cancellation failed",
        {
          description: isMissing
            ? "Your account doesn't have a Stripe or PayPal subscription on file — this can happen with complimentary access or if a previous cancellation already went through. Email info@recoverytrauma.com and we'll sort it out within a working day."
            : msg,
          action: isMissing
            ? {
                label: "Email support",
                onClick: () =>
                  window.open(
                    "mailto:info@recoverytrauma.com?subject=Cancel%20my%20Founding%20Membership",
                    "_blank",
                  ),
              }
            : undefined,
          duration: isMissing ? 15000 : 6000,
        },
      );
    } finally {
      setCancelPending(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      {isAdmin ? (
        <div className="mb-6 flex items-center gap-2 rounded-2xl border border-amber-400/50 bg-amber-50/60 px-4 py-3 text-sm text-amber-900">
          <ShieldAlert className="h-4 w-4" />
          <span>
            <strong>Admin view.</strong> This is the same page members see for managing their billing.
          </span>
        </div>
      ) : null}

      <div className="rounded-3xl border border-gold/40 bg-card p-8 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl text-foreground md:text-3xl">
              Your Membership
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage your billing, download receipts, or cancel any time.
            </p>
          </div>
          <Crown className="h-8 w-8 text-[var(--gold-deep)]" strokeWidth={1.5} />
        </div>

        {loadingMine ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your membership…
          </div>
        ) : !mine ? (
          <div className="mt-8 rounded-2xl border border-border bg-muted/30 p-5 text-sm">
            <p className="text-foreground">You don't have a membership yet.</p>
            <Link
              to="/membership"
              className="mt-3 inline-block text-sm font-medium text-[var(--gold-deep)] underline"
            >
              View Membership →
            </Link>
          </div>
        ) : (
          <>
            <dl className="mt-6 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  Status
                </dt>
                <dd className="mt-1 flex items-center gap-2 font-medium text-foreground">
                  {isActive ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <span className="capitalize">
                    {status?.replace(/_/g, " ") ?? "unknown"}
                  </span>
                  {hasPaypal && !paypalCancelled ? (
                <p className="w-full text-xs text-muted-foreground">
                  You pay by PayPal. Cancelling here stops all future PayPal
                  charges — you don't need to do anything in PayPal as well.
                </p>
              ) : null}

              {paypalCancelled ? (
                <p className="w-full text-xs text-muted-foreground">
                  Your PayPal subscription is cancelled — no further payments
                  will be taken.
                </p>
              ) : null}

              {isComp ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                      Complimentary
                    </span>
                  ) : null}
                </dd>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {cancelAtPeriodEnd ? "Access ends" : "Next billing date"}
                </dt>
                <dd className="mt-1 font-medium text-foreground">
                  {nextBilling ?? (isComp ? "No billing cycle" : "—")}
                </dd>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  Email on file
                </dt>
                <dd className="mt-1 font-medium text-foreground">
                  {mine.email ?? "—"}
                </dd>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  Payment method
                </dt>
                <dd className="mt-1 font-medium text-foreground">
                  {hasStripe || mine.stripe_customer_id
                    ? "Card (Stripe)"
                    : hasPaypal
                    ? "PayPal"
                    : "None on file"}
                </dd>
                {hasPaypal ? (
                  <dd className="mt-1 text-xs text-muted-foreground">
                    {mine.paypal?.paypal_payer_email
                      ? `${mine.paypal.paypal_payer_email} · `
                      : ""}
                    Subscription {mine.paypal?.paypal_subscription_id}
                    {paypalStatus ? ` · ${paypalStatus.toLowerCase()}` : ""}
                  </dd>
                ) : null}
              </div>
            </dl>

            <UpgradeMembershipCard onUpgraded={reload} hidden={autoOpenCancel} />


            {cancelAtPeriodEnd ? (
              <div className="mt-5 rounded-xl border border-amber-300/60 bg-amber-50/70 p-4 text-sm text-amber-900">
                <p className="font-medium">Cancellation scheduled.</p>
                <p className="mt-1">
                  Your membership will end on{" "}
                  <strong>{nextBilling ?? "your next billing date"}</strong>.
                  You'll keep full access until then.
                  {hasStripe ? " Resume any time from the Stripe portal." : ""}
                </p>
              </div>
            ) : null}

            {canReactivatePaypal ? (
              <ReactivatePaypalMembership className="mt-5" />
            ) : null}

            {!isActive && !appleManaged && !paypalLive && !canReactivatePaypal ? (
              <div className="mt-5 rounded-2xl border border-gold/30 bg-[var(--gold-soft)]/25 p-4">
                <p className="text-sm leading-relaxed text-foreground/90">
                  Renew your membership with PayPal — no card details needed. You&rsquo;ll
                  review and confirm everything inside PayPal before any payment is made.
                </p>
                <div className="mt-3">
                  <PaypalCheckoutButton />
                </div>
              </div>
            ) : null}

            {cancelledAt && !isActive ? (
              <div className="mt-5 rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                Membership ended on <strong>{cancelledAt}</strong>.
              </div>
            ) : null}

            {appleManaged ? (
              <div className="mt-5 rounded-xl border border-border bg-muted/30 p-4 text-sm text-foreground/85">
                <p className="font-medium">
                  Purchased through the Apple App Store
                </p>
                <p className="mt-1">
                  Apple manages this subscription, including renewals,
                  cancellation and payment method. Recovery Trauma™ cannot
                  cancel an Apple subscription for you.
                </p>
                <a
                  href={APPLE_SUBSCRIPTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold mt-3 inline-flex items-center gap-2 text-sm"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Manage Subscription with Apple
                </a>
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {!appleManaged && mine.stripe_customer_id ? (
                <button
                  onClick={openPortal}
                  disabled={portalPending}
                  className="btn-gold inline-flex items-center gap-2 text-sm disabled:opacity-60"
                >
                  {portalPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ExternalLink className="h-3.5 w-3.5" />
                  )}
                  Manage in Stripe
                </button>
              ) : null}

              {!appleManaged && hasPaypal ? (
                <a
                  href="https://www.paypal.com/myaccount/autopay/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold inline-flex items-center gap-2 text-sm"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Manage in PayPal
                </a>
              ) : null}

              {!appleManaged && ((isActive && hasStripe) || paypalLive) && !cancelAtPeriodEnd ? (
                <button
                  onClick={() => {
                    trackOperational("cancel_membership_clicked", { at: new Date().toISOString() });
                    trackOperational("cancellation_modal_opened", { trigger: "button" });
                    setCancelOpen(true);
                  }}
                  className="inline-flex items-center rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Cancel membership
                </button>
              ) : null}

              {hasPaypal && !paypalCancelled ? (
                <p className="w-full text-xs text-muted-foreground">
                  You pay by PayPal. Cancelling here stops all future PayPal
                  charges — you don't need to do anything in PayPal as well.
                </p>
              ) : null}

              {paypalCancelled ? (
                <p className="w-full text-xs text-muted-foreground">
                  Your PayPal subscription is cancelled — no further payments
                  will be taken.
                  {nextBilling ? ` Your access continues until ${nextBilling}.` : ""}
                </p>
              ) : null}

              {isComp ? (
                <p className="text-xs text-muted-foreground">
                  Complimentary access — no card on file. Contact support to
                  end it.
                </p>
              ) : null}
            </div>
          </>
        )}
      </div>


      <p className="mt-6 text-center text-xs text-muted-foreground">
        Questions? Email{" "}
        <a
          href="mailto:support@recoverytrauma.com"
          className="underline hover:text-foreground"
        >
          support@recoverytrauma.com
        </a>
        .
      </p>

      <Dialog
        open={cancelOpen}
        onOpenChange={(o) => {
          if (!o) trackOperational("cancellation_modal_closed", { action: "dismissed" });
          setCancelOpen(o);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Cancel your Membership?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm text-muted-foreground">
                <p>
                  You'll keep full access to journals, tools, community, and
                  events until{" "}
                  <strong className="text-foreground">
                    {nextBilling ?? "the end of your current billing period"}
                  </strong>
                  . No further charges will be made.
                </p>
                <p>
                  As a Member, your{" "}
                  <strong className="text-foreground">
                    lifetime founding price
                  </strong>{" "}
                  is only guaranteed while your membership stays active. If
                  you cancel and rejoin later, standard pricing will apply.
                </p>
                <p>
                  {hasStripe
                    ? "You can resume any time before your access ends from the Stripe portal — no data is lost."
                    : hasPaypal
                    ? "You can restart any time from this page — you'll be taken to PayPal to review and confirm before any payment is made. No data is lost."
                    : "You can resume any time before your access ends — no data is lost."}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-medium text-foreground">
              Before you go — what's the main reason you're leaving?{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </p>
            <p className="text-xs text-muted-foreground">
              One tap is enough — it simply helps me understand what to improve.
            </p>
            <div className="space-y-2">
              {[
                { value: "too_expensive", label: "It's too expensive for me right now" },
                { value: "not_using_it", label: "I'm not using it enough" },
                { value: "not_what_expected", label: "It wasn't what I expected" },
                { value: "found_alternative", label: "I found something else that suits me better" },
                { value: "technical_problems", label: "Something didn't work for me" },
                { value: "temporary_break", label: "I just need a break" },
                { value: "other", label: "Something else" },
                { value: "prefer_not_to_say", label: "I'd rather not say" },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={option.value}
                    checked={cancelReason === option.value}
                    onChange={() => setCancelReason(option.value)}
                    className="h-4 w-4 accent-[var(--gold-deep)]"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <textarea
              value={cancelDetail}
              onChange={(e) => setCancelDetail(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Anything else you'd like us to know? (optional)"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => {
                trackOperational("cancellation_modal_closed", { action: "keep_membership" });
                setCancelOpen(false);
              }}
              disabled={cancelPending}
              className="inline-flex items-center justify-center rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
            >
              Keep my membership
            </button>
            <button
              onClick={confirmCancel}
              disabled={cancelPending}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            >
              {cancelPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Yes, cancel at period end
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
